import { ApiError } from '../api';

/**
 * What a screen says while it is waiting, and what it says when the waiting failed.
 *
 * Before this, nothing said the second thing. Not one `useQuery` in the application displayed an
 * error — roughly a hundred of them across twenty-four components, and every single one was
 * silent on failure. Measured in the deployed app by making reads reject: after the retries were
 * exhausted the Habits screen settled on a "New habit" button and nothing else, Projects on "New
 * project" and nothing else. No message, no retry, no clue. The only reasonable conclusion
 * available to the user is that their data is gone.
 *
 * That is not an exotic failure either. This is a self-hosted app behind a container that
 * restarts, `refetchOnWindowFocus` is on, and a laptop coming out of sleep refetches every
 * visible query against a network that is not up yet.
 *
 * Two rules the component exists to enforce:
 *
 * 1. **A failed read is visible where the data would have been.** A toast in the corner is not
 *    where anyone is looking, and a screen that is blank for a reason still looks blank.
 * 2. **A failed read is never reported as an empty one.** "Nothing matches that" and "Nothing
 *    logged in this period" were both being rendered off `data ?? []` after a failure — a
 *    confident wrong answer, which is worse than no answer, because you act on it.
 */

/** The slice of a TanStack query this needs; typed structurally so any query fits. */
export interface QueryLike {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  isFetching: boolean;
  refetch: () => unknown;
}

const messageFor = (error: unknown): string => {
  // The API's own message when it gave one: "That goal is not yours" beats "Something failed".
  if (error instanceof ApiError) return error.message;
  return 'Could not reach the server.';
};

export function QueryState({
  query,
  what,
  className = 'muted',
}: {
  query: QueryLike;
  /** What could not be loaded — "your habits", "this week". Used in the failure sentence. */
  what?: string;
  /** So an early-return site can keep its own layout box (`detail muted`, and such). */
  className?: string;
}) {
  if (query.isPending) return <p className={className}>Loading…</p>;
  if (!query.isError) return null;

  return (
    <p className="error" data-testid="query-error" role="status">
      {messageFor(query.error)}
      {what ? ` Could not load ${what}.` : ''}{' '}
      {/*
        A retry button, because the alternative instruction is "reload the page", which throws
        away everything else on screen to fix one panel. `isFetching` rather than `isPending`:
        a query that has already failed is no longer pending while it retries, so guarding on
        pending would leave the button live and let it be pressed five times.
      */}
      <button className="link" disabled={query.isFetching} onClick={() => void query.refetch()}>
        {query.isFetching ? 'trying…' : 'try again'}
      </button>
    </p>
  );
}
